import { Injectable } from '@angular/core';
import { Headers, URLSearchParams } from '@angular/http';
import { Observable, throwError, forkJoin, of } from 'rxjs';
import { catchError, flatMap, map, tap } from 'rxjs/operators';
import { Api } from 'app/core/api.service';
import { Application, Binding, BindingRequest } from './application.model';
import { difference, first } from 'app/core/array/functions';
import { UserSettingsService } from 'app/user/user-settings.service';
import { ServiceType } from 'app/core/service-type.model';
import { toObject } from 'app/core/object/functions';

function formatError(errorResponse: Response) {
  if (errorResponse.status === 400) {
    const json = errorResponse.json() as any;
    const error =
      json.errorDescription || json.message || errorResponse.toString();
    return throwError(error);
  }
  return throwError(errorResponse.toString());
}

export const APPLICATIONS_COUNT = 10000;

@Injectable({
  providedIn: 'root'
})
export class ApplicationsService {
  private allApplications: { [id: string]: Application } = {};

  applications(
    serviceType: ServiceType = this._userSettingsService.serviceType
  ) {
    return Object.values(this.allApplications).filter(
      application => application.type === serviceType
    );
  }

  defaultApplication(
    serviceType: ServiceType = this._userSettingsService.serviceType
  ): Application {
    const defaultApplication = this._userSettingsService.defaultApplications[
      serviceType
    ];
    const application = defaultApplication && this.find(defaultApplication.id);
    if (!application || application.type !== serviceType) {
      return (this._userSettingsService.defaultApplication = first(
        this.applications(serviceType)
      ));
    }
    return application;
  }

  headers(applicationId: string = this.defaultApplication().id) {
    const headers = new Headers();
    headers.set('ACS-Application', applicationId);
    return headers;
  }

  constructor(
    private _api: Api,
    private _userSettingsService: UserSettingsService
  ) { }

  init() {
    return this.getApplications().pipe(
      map(applications => applications || []),
      flatMap(applications => {
        const requiredTypes = ['ic', 'dc'];
        const existingTypes = applications.map(application => application.type);
        const missingTypes = difference(requiredTypes, existingTypes);
        if (missingTypes.length === 0) {
          return of(applications);
        }
        return forkJoin(
          missingTypes.map(type => {
            if (type === 'dc') {
              return this.createApplication(
                'Other Documents Demo',
                type
              );
            } else {
              return this.createApplication(
                'Invoices Demo',
                type
              );
            }
          })
        ).pipe(
          map(createdApplications => applications.concat(createdApplications))
        );
      }),
      tap(
        applications =>
          (this.allApplications = toObject(applications, app => app.id))
      )
    );
  }

  search(query: string) {
    return this.applications().filter((app: Application) =>
      this.filterRows(app, query)
    );
  }

  getApplicationsCount() {
    const params = new URLSearchParams();
    params.set('type', this._userSettingsService.serviceType);
    return this._api.get('/v2/applications/count', { params });
  }

  filterRows(app: Application, query: string) {
    const _query = query.toLowerCase();
    return (
      app.name.toLowerCase().includes(_query) ||
      app.id.toLowerCase().includes(_query) ||
      app.project.toLowerCase().includes(_query) ||
      (app.tenant && app.tenant.toLowerCase().includes(_query))
    );
  }

  searchByName(query: string) {
    return this.applications().filter((app: Application) =>
      app.name.toLowerCase().includes(query.toLowerCase())
    );
  }

  find(id: string) {
    return this.allApplications[id];
  }

  setDefault(application: Application): void {
    this._userSettingsService.defaultApplication = application;
  }

  getApplications(
    serviceType: ServiceType = null,
    search: string = null,
    includeDeleted = 'false',
    skip: number = 0,
    take: number = APPLICATIONS_COUNT // TODO(AKryuchkov): Плохо так делать. Сделать пагинацию на бэке
  ): Observable<Application[]> {
    const params = new URLSearchParams();
    params.set('type', serviceType);
    params.set('search', search);
    params.set('take', `${take}`);
    params.set('skip', `${skip}`);
    params.set('includeDeleted', includeDeleted);
    return this._api.get('/v2/applications/', { params });
  }

  generateBasicAuthHeaders(login: string, password: string): Headers {
    const credentials = `${login}:${password}`;
    const headers = new Headers();
    headers.set('authorization', `Basic ${btoa(credentials)}`);
    return headers;
  }

  getProjects(
    tenant: string,
    login: string,
    password: string
  ): Observable<string[]> {
    return this._api.get(`/v2/application/projects/${tenant}`, {
      headers: this.generateBasicAuthHeaders(login, password)
    });
  }

  createApplication(name: string, type: string): Observable<Application> {
    return this._api
      .post('/v2/application/', { name, type })
      .pipe(
        tap(application => (this.allApplications[application.id] = application))
      );
  }

  deleteApplication(id: string): Observable<void> {
    return this._api.delete(`/v2/application/${id}`).pipe(
      tap(() => {
        const application = this.defaultApplication();
        delete this.allApplications[id];
        if (application.id === id) {
          this._userSettingsService.defaultApplication = first(
            this.applications()
          );
        }
      })
    );
  }

  restetToken(id: string): Observable<Application> {
    return this._api
      .put(`/v2/application/token`, { id })
      .pipe(tap(this.updateApplication.bind(this)));
  }

  renameApplication(id: string, name: string) {
    return this._api
      .put(`/v2/application/name`, { id, name })
      .pipe(tap(this.updateApplication.bind(this)));
  }

  copyApplication(id: string) {
    const headers = new Headers();
    headers.set('content-type', 'application/javascript');

    return this._api
      .post(`/v2/application/clone`, { ApplicationId: id }, { headers })
      .pipe(
        tap(application => (this.allApplications[application.id] = application))
      );
  }

  private updateApplication(application: Application) {
    this.allApplications[application.id] = Object.assign(
      this.allApplications[application.id],
      application
    );
  }

  updateApplications(applications: Application[]) {
    applications.map(application => {
      this.allApplications[application.id] = { ...application };
    });
  }

  findUserApplications(email: string): Observable<Application[]> {
    const params = { owner: email };
    const skip = 0;
    const take = APPLICATIONS_COUNT; // TODO(AKryuchkov): Плохо так делать. Сделать пагинацию на бэке
    return this._api
      .get(`/v2/system/applications/${skip}/${take}`, { params })
      .pipe(catchError(formatError));
  }

  updateBinding(application: Application): Observable<any> {
    const body: BindingRequest = {
      id: application.id,
      tenant: application.binding.tenant,
      project: application.binding.project,
      user: application.binding.user,
      password: application.binding.password,
      is_realtime: application.binding.isRealtime
        ? application.binding.isRealtime
        : false
    };
    return this._api.put('/v2/system/application/binding/', body).pipe(
      tap(() => {
        if (application.type === 'ic') {
          this._api
            .post(
              `/v2/system/dataset/rebind/`,
              { application: application.id },
              { headers: this.headers() }
            )
            .subscribe();
        }
      })
    );
  }

  bindingApplication(binding: Binding): Observable<any> {
    const body: Binding = {
      id: binding.id,
      tenant: binding.tenant,
      project: binding.project,
      user: binding.user,
      password: binding.password
    };
    return this._api
      .put('/v2/application/binding/', body)
      .pipe(
        tap(application => (this.allApplications[application.id] = application))
      );
  }
}
